import React, { useEffect } from 'react';
import {
    generatePath,
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import { Map } from 'immutable';
import {
    fetchChatSession,
    fetchPublicChatSessions,
    getChatSessionDetails,
    leaveRoom,
    showImagePreview,
    updateChatSession,
} from 'flow/actions/chat-actions';
import { fetchCloudFiles } from 'flow/actions/social-integration-actions';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import Button from 'flow/components/ui-components/Button';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import RouteLink from 'flow/components/ui-components/RouteLink';
import SettingsButton from 'flow/components/ui-components/SettingsButton';
import Toggle from 'flow/components/ui-components/Toggle';
import VideoCallButton from 'flow/components/VideoCallButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getContact } from 'flow/selectors/contact-selectors';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';
import { getExtensionKeyFromSession } from 'flow/selectors/selectors';
import { ChatMember, ChatSession, Details } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getChatRoomAvatar } from 'flow/utils';
import { Capabilities } from 'flow/utils/capability-utils';
import { getCallableNumberFromContact } from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import Archive from './Archive';
import { roomTypes } from './MessageInput';
import Mute from './Mute';
import ActiveProfileStatus from 'flow/components/ui-components/ActiveProfileStatus';

const MessagesDetailsPage = () => {
    const { i18next } = useAppContext();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    useEffect(() => {
        dispatch(getChatSessionDetails(id));
        dispatch(fetchChatSession(id, 'contact'));
        dispatch(fetchCloudFiles(id));
    }, [dispatch, id]);

    const ownUserHasCapabilityVideo = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.VIDEO),
    );

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', id]),
    );

    const fileDetails: TypedMap<Details> = useAppSelector((state) =>
        state.chats.getIn(['details', id], Map()),
    );

    const cloudFiles = useAppSelector((state) =>
        state.socialIntegration.get('cloudFiles'),
    );

    const { hasFeature } = useFeatureFlags();

    const isAISummarizedEnabled = hasFeature(
        FeatureFlags.AI_SUMMARIZE_UNREAD_MESSAGES,
    );

    const history = useHistory();

    return (
        <Page
            header={i18next.t('Chat.Details.Title')}
            onClickBack={() =>
                history.push(generatePath(routes['messages.id'], { id }))
            }
        >
            <DetailsHeader session={session} />

            <Divider />

            <RouteLink
                iconKey="group"
                to={generatePath(routes['messages.id.members'], { id })}
                description={session?.get('members')?.size}
            >
                {i18next.t('chatsettings.members')}
            </RouteLink>

            {ownUserHasCapabilityVideo && (
                <RouteLink
                    iconKey="video"
                    to={generatePath(routes['messages.id.lobby'], { id })}
                >
                    {i18next.t('videoconference.general.meeting_room')}
                </RouteLink>
            )}

            <Mute />

            {isAISummarizedEnabled && (
                <SettingsButton
                    iconKey="autoFixWand"
                    onClick={() =>
                        openModal('generateChatSummary', { chatSessionKey: id })
                    }
                >
                    {i18next.t('ai.chat.summary.button.tooltip.summarize_all')}
                </SettingsButton>
            )}

            <RoomOption session={session} />

            <Archive />

            <Divider />

            {fileDetails?.get('numberOfImages') ? (
                <RouteLink
                    iconKey="image"
                    to={generatePath(routes['messages.id.images'], { id })}
                    description={fileDetails.get('numberOfImages')}
                >
                    {i18next.t('Chat.Details.Images')}
                </RouteLink>
            ) : null}

            {fileDetails?.get('numberOfFiles') ? (
                <RouteLink
                    iconKey="file"
                    to={generatePath(routes['messages.id.files'], { id })}
                    description={fileDetails.get('numberOfFiles')}
                >
                    {i18next.t('Chat.Details.Files')}
                </RouteLink>
            ) : null}

            {fileDetails?.get('numberOfPosts') ? (
                <RouteLink
                    iconKey="message_post"
                    to={generatePath(routes['messages.id.posts'], { id })}
                    description={fileDetails.get('numberOfPosts')}
                >
                    {i18next.t('Chat.Details.Posts')}
                </RouteLink>
            ) : null}

            {cloudFiles?.getIn(['status', 'active']) &&
            cloudFiles.getIn([id, 'files'])?.size > 0 ? (
                <RouteLink
                    iconKey="drive"
                    to={generatePath(routes['messages.id.drive'], { id })}
                    description={cloudFiles.getIn([id, 'files']).size}
                >
                    {'Google Drive'}
                </RouteLink>
            ) : null}
        </Page>
    );
};

interface RoomOptionProps {
    session: TypedMap<ChatSession>;
}

const RoomOption = ({ session }: RoomOptionProps) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownExtension } = useExtension();
    const { openModal } = useModal();
    const { id } = useParams<{ id: ChatSessionKey }>();

    const history = useHistory();

    if (!session) {
        return null;
    }

    const isChatRoom = session.get('isChatRoom');
    const roomType = session.get('roomType');

    if (
        isChatRoom &&
        session.get('admins').includes(ownExtension.get('chatUserKey'))
    ) {
        const handleRoomType = () => {
            const chatSessionDTO = {
                key: id,
                roomType:
                    roomType === roomTypes.public
                        ? roomTypes.room
                        : roomTypes.public,
            };

            dispatch(
                updateChatSession(id, chatSessionDTO, () =>
                    dispatch(fetchPublicChatSessions()),
                ),
            );
        };
        return (
            <SettingRow
                cta={
                    <Toggle
                        value={roomType === roomTypes.public}
                        onChange={handleRoomType}
                        text={i18next.t('chatsettings.public_room')}
                    />
                }
            >
                <Icon iconKey="public" />
                <p className="text-base/5 first-letter:uppercase">
                    {i18next.t('chatsettings.public_room')}
                </p>
            </SettingRow>
        );
    }

    if (isChatRoom && roomType === 'public') {
        const handleLeaveRoom = () => {
            dispatch(leaveRoom(id, ownExtension.get('chatUserKey')));
            history.push(routes['messages']);
        };

        return (
            <SettingsButton iconKey="leave" onClick={handleLeaveRoom}>
                {i18next.t('chatsettings.leave_room')}
            </SettingsButton>
        );
    }

    if (!isChatRoom) {
        return (
            <SettingsButton
                iconKey="addBox"
                onClick={() =>
                    openModal('manageRoom', {
                        sessionKey: id,
                        header: i18next.t('chat.create_as_room'),
                        confirmButtonText: i18next.t('chat.create_as_room'),
                    })
                }
            >
                {i18next.t('chat.create_as_room')}
            </SettingsButton>
        );
    }

    return null;
};

interface DetailsHeaderProps {
    session: TypedMap<ChatSession>;
}

const DetailsHeader = ({ session }: DetailsHeaderProps) => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const history = useHistory();
    const { pathname } = useLocation();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: ChatSessionKey }>();

    if (!session) {
        return null;
    }

    const handleClick = () => {
        const member: TypedMap<ChatMember> = session
            .get('members')
            .find((m) => m.get('extensionKey') !== ownExtensionKey);

        history.push(
            generatePath(routes['messages.id.settings.contact.key'], {
                extensionKey: member.get('extensionKey'),
                id,
            }),
            { from: pathname },
        );
    };

    const roomImageSrc = session.get('roomImageResource');

    const handelShowImage = () => {
        if (!roomImageSrc) {
            return;
        }
        dispatch(showImagePreview(getChatRoomAvatar(roomImageSrc)));
    };

    const isGroupChat =
        session.get('isChatRoom') || session.get('members').size > 2;

    return (
        <div
            className={twMerge(
                'group flex items-center justify-between rounded-sm p-2 hover:bg-gray-100',
                !isGroupChat
                    ? 'cursor-pointer'
                    : roomImageSrc
                      ? 'cursor-pointer'
                      : '',
            )}
            onClick={!isGroupChat ? handleClick : handelShowImage}
        >
            <div className="flex items-center gap-x-3">
                <ChatAvatar
                    session={session}
                    size="large"
                    showUnreadBadge={false}
                />
                <div className="flex flex-col">
                    <p className="text-gray-800">{session.get('name')}</p>
                    <SessionDetails session={session} />
                </div>
            </div>

            <DetailsAction
                session={session}
                className="reveal-on-hover-or-focus block"
            />
        </div>
    );
};

interface SessionDetailsProps {
    session: TypedMap<ChatSession>;
}

const SessionDetails = ({ session }: SessionDetailsProps) => {
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const extensionKey = session
        ?.get('members')
        ?.find((m) => m.get('extensionKey') !== ownExtensionKey)
        ?.get('extensionKey');

    if (session.get('isChatRoom')) {
        return (
            <p className="text-sm/4 text-gray-500">
                {session.get('roomDescription')}
            </p>
        );
    }

    if (session.get('members').size > 2) {
        return null;
    }

    return (
        <p className="text-sm/4 text-gray-500">
            <ActiveProfileStatus extensionKey={extensionKey} />
        </p>
    );
};

interface DetailsActionProps {
    session: TypedMap<ChatSession>;
    className: string;
}

const DetailsAction = ({ session, className }: DetailsActionProps) => {
    const { i18next } = useAppContext();
    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { openModal } = useModal();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const { hasFeature } = useFeatureFlags();

    const ownUserHasCapabilityVideo = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.VIDEO),
    );
    const ownUserHasCapabilityVideo1on1 = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );

    const contactKey = session
        .get('members')
        .find((m) => m.get('extensionKey') !== ownExtensionKey)
        ?.getIn(['contact', 'key']);

    const extensionKey = getExtensionKeyFromSession(session, ownExtensionKey);

    const contact = useAppSelector((state) =>
        getContact(state, contactKey, extensionKey),
    );

    if (!ownExtensionKey) {
        return null;
    }

    const isAdmin = session
        .get('admins')
        .includes(ownExtension.get('chatUserKey'));

    if (session.get('isChatRoom') && isAdmin) {
        return (
            <Button
                className={twMerge('size-10 bg-gray-300', className)}
                iconKey="edit"
                onClick={(event) => {
                    event.stopPropagation();
                    openModal(
                        session.get('roomType') === roomTypes.room
                            ? 'manageRoom'
                            : 'managePublicRoom',
                        {
                            sessionKey: id,
                            editRoom: true,
                            header: i18next.t('Tooltip.Nav.Title.Edit'),
                            confirmButtonText: i18next.t('navigate.save'),
                        },
                    );
                }}
            />
        );
    }

    if (session.get('isChatRoom') || session.get('members').size > 2) {
        return null;
    }

    const shouldShowVideoIcon =
        ownUserHasCapabilityVideo &&
        ownUserHasCapabilityVideo1on1 &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    return (
        <div className="flex gap-3">
            {shouldShowVideoIcon && (
                <VideoCallButton
                    extensionKey={extensionKey}
                    chatSessionKey={id}
                />
            )}

            <CallAdaptiveButton
                phoneNumber={getCallableNumberFromContact(contact)}
                extensionKey={extensionKey}
                transferContact={contact}
                size="medium"
                activeBg={true}
                bgColor="bg-gray-200"
            />
        </div>
    );
};

export default MessagesDetailsPage;
